import { buildJupiterStep, buildStonUsdtToTonStep } from './step-builders'
import type { RouteContext, RouteStep, RouteStrategy, StepStatus } from './types'
import { findStep } from '@/features/crosschain-buy/domain/progress'

class LayerswapStrategy implements RouteStrategy {
  compute(ctx: RouteContext): RouteStep[] {
    const tonUsdSuffix = ctx.bridgeOutputUsd ? ` (${ctx.bridgeOutputUsd})` : ''
    const steps: RouteStep[] = []
    const jupiterStep = findStep(ctx.state, 'jupiter_swap')

    if (!ctx.skipJupiterSwap || jupiterStep) {
      const jupiterOutputLabel = ctx.jupiterUsdtOutput
        ? `~${ctx.jupiterUsdtOutput} USDT`
        : '... USDT'
      steps.push(
        buildJupiterStep(
          ctx.sourceAmount,
          ctx.fromToken,
          jupiterOutputLabel,
          ctx.state,
          ctx.step1Status,
          ctx.jupiterTxUrl
        )
      )
    }

    // A completed pre-swap, direct USDT route, or submitted bridge all enter the bridge phase.
    const bridgePhaseEntered =
      jupiterStep?.status === 'completed' ||
      ctx.skipJupiterSwap ||
      !!findStep(ctx.state, 'bridge')?.txHash
    const bridgeStatus: StepStatus = bridgePhaseEntered ? ctx.step1Status : 'pending'
    const bridgeAmountInPrefix = ctx.jupiterUsdtOutput ? `${ctx.jupiterUsdtOutput} ` : ''
    const bridgeAmountOut = ctx.bridgeOutputAmount ? `~${ctx.bridgeOutputAmount}` : '...'
    const bridgeOutputLabel = ctx.bridgeOutputToken === 'TON' ? 'GRAM' : ctx.bridgeOutputToken

    steps.push({
      key: 'bridge',
      label: 'Bridge via Layerswap',
      amountLine: `${bridgeAmountInPrefix}USDT (Solana) → ${bridgeAmountOut} ${bridgeOutputLabel}${tonUsdSuffix}`,
      amountLoading: false,
      status: bridgeStatus,
      txLinks: ctx.bridgeTxLinks,
      etaHint: ctx.bridgeEtaHint,
    })

    const stonCtx = {
      previewLoading: ctx.previewLoading,
      tonAmount: ctx.tonAmount,
      spottyAmount: ctx.spottyAmount,
      bridgeOutputAmount: ctx.bridgeOutputAmount,
    }

    if (ctx.bridgeOutputToken !== 'TON') {
      steps.push(buildStonUsdtToTonStep(stonCtx, ctx.step2Status, ctx.tonUsdtToTonTxUrl))
    }

    const spottyOut = ctx.spottyAmount ? `~${ctx.spottyAmount}` : '...'
    const tonToSpottyAmount =
      ctx.bridgeOutputToken === 'TON' ? ctx.bridgeOutputAmount : ctx.tonAmount
    const tonToSpottyStatus = ctx.bridgeOutputToken === 'TON' ? ctx.step2Status : ctx.step3Status
    steps.push({
      key: 'ton_to_spotty',
      label: 'Swap via STON.fi',
      amountLine: tonToSpottyAmount
        ? `${tonToSpottyAmount} GRAM → ${spottyOut} SPOTTY`
        : 'GRAM → SPOTTY',
      amountLoading: ctx.previewLoading && !ctx.spottyAmount,
      status: tonToSpottyStatus,
      txLinks:
        tonToSpottyStatus !== 'pending' && ctx.tonTxUrl
          ? [{ url: ctx.tonTxUrl, label: 'View tx' }]
          : [],
      etaHint: null,
    })

    return steps
  }
}

export const layerswapStrategy = new LayerswapStrategy()
